// The dialogs that act on one Windows image or game disk (spec/25 §10, §16), each centred over the page it is opened from
// (Windows images or Games):
//
//   VersionsDialog       the version timeline: every ready version is a restore point; a version's preparation report;
//                        Restore; Move the rest for a game disk from a folder that did not finish; and Merge old versions,
//                        which lists the restore points it takes away
//   ChangeWindowsDialog  Change Windows using a PC: pick an idle PC to start the Windows on, to change it there
//   UpdateGamesDialog    Update games: open a new version of a game disk on its drive letter on the boot server, after
//                        saying what happens
//   SaveChangesDialog    Save and roll out: the note for the new version — and, for a change of Windows made on a PC,
//                        whether it is saved as a new version of the image or as a new image of its own;
//                        ThrowAwayDialog throws the changes away
//   DependencyGuard   an image saved from this one stands on one of its versions: said, with Make <image> independent
//   MakeIndependentDialog  asks first, then the boot server flattens the image's first version into a base of its own
//   DriveLetterDialog a game disk's drive letter, among the ones no other game disk holds
//   NicDriversDialog  which network drivers go into the image: those seen at the venue, those chosen, or all

import type { BootImage, BootImageVersion, BootNicDriverMode, BootPc } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, EmptyState, RadioGroup, SegmentedControl, SelectField, Skeleton, TextField, useToast } from '@clavian/ui-web';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { problemDetail } from '../api/problems';
import { Notice } from '../ui';
import { ConfirmDialog } from './ConfirmDialog';
import { formatBytes, freeDriveLetters, mergeRemoves, sortNewestFirst } from './format';
import { PreparationReport } from './PreparationReport';
import {
  useBootImages,
  useBootPcs,
  useBootVersions,
  useDiscardBootEdit,
  useMakeBootImageIndependent,
  useMergeBootImage,
  usePrepareBootVersionAgain,
  usePublishBootEdit,
  useRestoreBootImage,
  useStartBootEdit,
  useStartBootServerEdit,
  useUpdateBootImage,
  type BootScope,
} from './queries';
import { useBootStations } from './stations';

/** A game disk made from a folder whose job failed: the boot server can run the folder job again. */
export const isFailedFolderVersion = (version: BootImageVersion): boolean => version.state === 'failed' && version.source === 'folder';

/** The label of the way on after a folder job failed: a move carries on with what is still in the folder; a copy starts over. */
export const folderRetryLabelKey = (version: BootImageVersion): string => (version.folderMode === 'move' ? 'boot.images.versions.moveRest' : 'boot.images.versions.tryAgain');

/**
 * Move the rest (or Try again, for a copy): the failed version goes back to preparing and the boot server runs the folder
 * job again — for a move, over what is still in the folder. No restart is needed. Says so in a toast.
 */
export function useRetryFolder(scope: BootScope): { readonly pending: boolean; readonly retry: (version: BootImageVersion) => void } {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const prepare = usePrepareBootVersionAgain(scope);
  return {
    pending: prepare.isPending,
    retry: (version) =>
      prepare.mutate(
        { imageId: version.imageId, versionId: version.id },
        {
          onSuccess: () => show({ tone: 'ok', title: t(version.folderMode === 'move' ? 'boot.images.versions.moveRestDone' : 'boot.images.versions.tryAgainDone') }),
          onError: (caught) => show({ tone: 'danger', title: problemDetail(caught) ?? t('boot.images.versions.moveRestFailed') }),
        },
      ),
  };
}

/** The PC a change of Windows is being made on, in the words the Floor uses (its station, or its network address). */
function useChangedOn(scope: BootScope, image: BootImage, enabled: boolean): string | undefined {
  const pcs = useBootPcs(scope, enabled);
  const stations = useBootStations(scope.organizationId, scope.venueId);
  const edit = image.openEdit;
  if (edit === null || edit.target === 'server' || edit.bootPcId === null) return undefined;
  const pc = pcs.data?.find((one) => one.id === edit.bootPcId);
  if (pc === undefined) return undefined;
  return pc.stationId === null ? pc.mac : (stations.get(pc.stationId)?.label ?? pc.mac);
}

interface ImageDialogProps {
  readonly scope: BootScope;
  readonly image: BootImage;
  readonly open: boolean;
  readonly onClose: () => void;
}

// -- Versions, restore and merge ---------------------------------------------------------------------

const VERSION_TONE = { published: 'ok', capturing: 'info', preparing: 'info', editing: 'info', failed: 'danger', discarded: 'neutral', merged: 'neutral' } as const;

export function VersionsDialog({ scope, image, open, onClose, canManage }: ImageDialogProps & { readonly canManage: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const versions = useBootVersions(scope, open ? image.id : undefined);
  const [report, setReport] = useState<BootImageVersion | undefined>(undefined);
  const [restoring, setRestoring] = useState<BootImageVersion | undefined>(undefined);
  const [preparingFrom, setPreparingFrom] = useState<BootImageVersion | undefined>(undefined);
  const [merging, setMerging] = useState(false);
  const restore = useRestoreBootImage(scope);
  const prepare = usePrepareBootVersionAgain(scope);
  const folder = useRetryFolder(scope);
  const { show } = useToast();
  const list = useMemo(() => sortNewestFirst(versions.data ?? []).filter((version) => version.state !== 'discarded'), [versions.data]);
  const published = list.filter((version) => version.state === 'published');

  let body: ReactNode;
  if (versions.data === undefined) {
    body = versions.isError ? (
      <Notice tone="danger" role="alert">
        {t('boot.images.versions.failed')}
      </Notice>
    ) : (
      <Skeleton height="10rem" />
    );
  } else if (list.length === 0) {
    body = <EmptyState size="compact" title={t('boot.images.versions.none')} />;
  } else {
    body = (
      <ol className="console-boot__timeline" aria-label={t('boot.images.versions.label', { name: image.name })}>
        {list.map((version) => (
          <VersionRow
            key={version.id}
            version={version}
            current={version.id === image.currentVersionId}
            canManage={canManage}
            onReport={() => setReport(version)}
            onRestore={() => setRestoring(version)}
            preparing={prepare.isPending || folder.pending}
            onRetryFolder={() => folder.retry(version)}
            onPrepareAgain={() =>
              // A published version is never changed: it asks first, and the answer is a new version made from it.
              version.state === 'published'
                ? setPreparingFrom(version)
                : prepare.mutate(
                    { imageId: image.id, versionId: version.id },
                    {
                      onSuccess: () => show({ tone: 'ok', title: t('boot.images.versions.prepareAgainDone', { number: version.number }) }),
                      onError: (caught) => show({ tone: 'danger', title: problemDetail(caught) ?? t('boot.images.versions.prepareAgainFailed') }),
                    },
                  )
            }
          />
        ))}
      </ol>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => (next ? undefined : onClose())}
        size="lg"
        title={t('boot.images.versions.title', { name: image.name })}
        description={t('boot.images.versions.intro')}
        footer={
          <>
            {canManage && published.length > 2 ? (
              <Button variant="secondary" onClick={() => setMerging(true)}>
                {t('boot.images.versions.merge')}
              </Button>
            ) : null}
            <Button variant="primary" onClick={onClose}>
              {t('common.close')}
            </Button>
          </>
        }
      >
        <div className="console-form">
          {image.pendingMergeUpToVersionId === null ? null : (
            <Notice tone="info" role="status">
              {t('boot.images.versions.mergePending')}
            </Notice>
          )}
          {body}
        </div>
      </Dialog>
      <Dialog
        open={report !== undefined}
        onOpenChange={(next) => (next ? undefined : setReport(undefined))}
        size="md"
        title={t('boot.report.title', { number: report?.number ?? 0 })}
        description={t('boot.report.intro')}
      >
        <PreparationReport report={report?.preparationReport} />
      </Dialog>
      <ConfirmDialog
        open={restoring !== undefined}
        onClose={() => setRestoring(undefined)}
        title={t('boot.images.restore.title', { number: restoring?.number ?? 0 })}
        description={t('boot.images.restore.body', { name: image.name })}
        confirmLabel={t('boot.images.restore.confirm')}
        cancelLabel={t('common.cancel')}
        failedText={t('boot.images.restore.failed')}
        onConfirm={() => (restoring === undefined ? Promise.resolve() : restore.mutateAsync({ imageId: image.id, versionId: restoring.id }))}
      />
      <ConfirmDialog
        open={preparingFrom !== undefined}
        onClose={() => setPreparingFrom(undefined)}
        title={t('boot.images.prepareFromPublished.title', { number: preparingFrom?.number ?? 0 })}
        description={t('boot.images.prepareFromPublished.body', { number: preparingFrom?.number ?? 0, name: image.name })}
        confirmLabel={t('boot.images.prepareFromPublished.confirm')}
        cancelLabel={t('common.cancel')}
        failedText={t('boot.images.prepareFromPublished.failed')}
        onConfirm={() =>
          preparingFrom === undefined
            ? Promise.resolve()
            : prepare.mutateAsync({ imageId: image.id, versionId: preparingFrom.id }).then((made) => {
                show({ tone: 'ok', title: t('boot.images.prepareFromPublished.done', { number: made.number, from: preparingFrom.number }) });
              })
        }
      />
      {merging ? <MergeDialog scope={scope} image={image} versions={published} onClose={() => setMerging(false)} /> : null}
    </>
  );
}

function VersionRow({
  version,
  current,
  canManage,
  onReport,
  onRestore,
  preparing,
  onPrepareAgain,
  onRetryFolder,
}: {
  readonly version: BootImageVersion;
  readonly current: boolean;
  readonly canManage: boolean;
  readonly onReport: () => void;
  readonly onRestore: () => void;
  readonly preparing: boolean;
  readonly onPrepareAgain: () => void;
  readonly onRetryFolder: () => void;
}) {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const when = version.publishedAt ?? version.createdAt;
  return (
    <li className="console-boot__version">
      <div className="console-boot__version-main">
        <div className="console-boot__version-head">
          <strong>{t('boot.images.versions.number', { number: version.number })}</strong>
          {current ? <Badge tone="accent">{t('boot.images.versions.current')}</Badge> : null}
          <Badge tone={VERSION_TONE[version.state]} dot>
            {t(`boot.images.versions.states.${version.state}`)}
          </Badge>
          {version.state === 'published' && !current ? <Badge tone="neutral">{t('boot.images.versions.restorePoint')}</Badge> : null}
        </div>
        <p className="console-muted">
          {format.dateTime(when, { date: 'medium', time: 'short' })}
          {version.sizeBytes === null ? '' : ` · ${formatBytes(version.sizeBytes)}`}
        </p>
        {version.note === null || version.note === '' ? null : <p>{version.note}</p>}
        {isFailedFolderVersion(version) ? <p className="console-muted">{t(version.folderMode === 'move' ? 'boot.games.failed.bodyMove' : 'boot.games.failed.bodyCopy')}</p> : null}
      </div>
      <div className="console-boot__version-actions">
        {version.preparationReport === null ? null : (
          <Button variant="ghost" size="sm" onClick={onReport}>
            {t('boot.images.versions.viewReport')}
          </Button>
        )}
        {/* A captured or imported version has no parent; an edit version does, and is not prepared again this way. A published one
            is prepared again as a new version made from it. */}
        {canManage &&
        version.parentVersionId === null &&
        ((version.state === 'failed' && version.preparationReport !== null) || version.state === 'published') ? (
          <Button variant="secondary" size="sm" disabled={preparing} onClick={onPrepareAgain}>
            {t('boot.images.versions.prepareAgain')}
          </Button>
        ) : null}
        {canManage && isFailedFolderVersion(version) ? (
          <Button variant="secondary" size="sm" disabled={preparing} onClick={onRetryFolder}>
            {t(folderRetryLabelKey(version))}
          </Button>
        ) : null}
        {canManage && version.state === 'published' && !current ? (
          <Button variant="secondary" size="sm" onClick={onRestore}>
            {t('boot.images.versions.restore')}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

/**
 * An image saved from this one stands on one of its versions (“Save as a new image”): the version cannot be merged away nor
 * the image archived until that image is made independent. Says which image and version, and offers Make <image>
 * independent (`boot:manage`), which opens its own small confirm in place of this dialog's action.
 */
export function DependencyGuard({
  scope,
  dependent,
  why,
  canManage,
}: {
  readonly scope: BootScope;
  readonly dependent: NonNullable<BootImage['dependents']>[number];
  readonly why: 'merge' | 'archive';
  readonly canManage: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const images = useBootImages(scope);
  const [asking, setAsking] = useState(false);
  const target = (images.data ?? []).find((image) => image.id === dependent.imageId);
  return (
    <Notice
      tone="warn"
      title={t('boot.images.dependency.title')}
      action={
        canManage && target !== undefined && target.pendingMergeUpToVersionId === null ? (
          <Button variant="secondary" size="sm" onClick={() => setAsking(true)}>
            {t('boot.images.dependency.action', { name: dependent.name })}
          </Button>
        ) : undefined
      }
    >
      <p>{t(`boot.images.dependency.${why}`, { name: dependent.name, number: dependent.onVersionNumber })}</p>
      {target?.pendingMergeUpToVersionId == null ? null : <p className="console-muted">{t('boot.images.independent.started', { name: dependent.name })}</p>}
      {asking && target !== undefined ? <MakeIndependentDialog scope={scope} image={target} onClose={() => setAsking(false)} /> : null}
    </Notice>
  );
}

/** Make an image independent: asks first, says what it does and that its PCs must be off, then the job runs on the server. */
export function MakeIndependentDialog({ scope, image, onClose }: { readonly scope: BootScope; readonly image: BootImage; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const make = useMakeBootImageIndependent(scope);
  const from = image.basedOn?.name ?? '';
  return (
    <ConfirmDialog
      open
      onClose={onClose}
      title={t('boot.images.independent.title', { name: image.name })}
      description={t('boot.images.independent.body', { name: image.name, from })}
      confirmLabel={t('boot.images.independent.confirm')}
      cancelLabel={t('common.cancel')}
      failedText={t('boot.images.independent.failed')}
      onConfirm={async () => {
        await make.mutateAsync(image.id);
        show({ tone: 'ok', title: t('boot.images.independent.started', { name: image.name }) });
      }}
    >
      <p className="console-muted">{t('boot.images.independent.off', { name: image.name })}</p>
    </ConfirmDialog>
  );
}

function MergeDialog({ scope, image, versions, onClose }: { readonly scope: BootScope; readonly image: BootImage; readonly versions: readonly BootImageVersion[]; readonly onClose: () => void }) {
  const { t } = useTranslation('console');
  const merge = useMergeBootImage(scope);
  // Merging up to the oldest version removes nothing, so it is not offered.
  const targets = versions.filter((version) => mergeRemoves(versions, version).length > 0);
  // Newest first: the default merges everything older than the version in use.
  const [upTo, setUpTo] = useState(targets[0]?.id ?? '');
  const chosen = targets.find((version) => version.id === upTo);
  const removed = chosen === undefined ? [] : mergeRemoves(versions, chosen);
  // An image saved from this one standing on a version the merge removes: the merge waits until it is independent.
  const removedNumbers = new Set(removed.map((version) => version.number));
  const blocking = (image.dependents ?? []).find((dependent) => removedNumbers.has(dependent.onVersionNumber));

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      size="md"
      title={t('boot.images.merge.title', { name: image.name })}
      description={t('boot.images.merge.intro')}
      confirmLabel={t('boot.images.merge.confirm', { count: removed.length })}
      cancelLabel={t('common.cancel')}
      tone="danger"
      failedText={t('boot.images.merge.failed')}
      confirmDisabled={blocking !== undefined}
      onConfirm={() => (chosen === undefined || blocking !== undefined ? Promise.resolve() : merge.mutateAsync({ imageId: image.id, upToVersionId: chosen.id }))}
    >
      {blocking === undefined ? null : <DependencyGuard scope={scope} dependent={blocking} why="merge" canManage />}
      <SelectField
        label={t('boot.images.merge.upTo')}
        value={upTo}
        onValueChange={setUpTo}
        options={targets.map((version) => ({ value: version.id, label: t('boot.images.merge.option', { number: version.number }) }))}
      />
      <div role="group" aria-label={t('boot.images.merge.removesLabel')}>
        <p>{t('boot.images.merge.removes', { count: removed.length })}</p>
        <ul className="console-boot__report-changes">
          {removed.map((version) => (
            <li key={version.id}>{t('boot.images.merge.removed', { number: version.number, note: version.note ?? '' })}</li>
          ))}
        </ul>
        {chosen === undefined ? null : <p className="console-muted">{t('boot.images.merge.keeps', { number: chosen.number })}</p>}
      </div>
    </ConfirmDialog>
  );
}

// -- Change Windows using a PC ------------------------------------------------------------------------

export function ChangeWindowsDialog({ scope, image, open, onClose }: ImageDialogProps): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const pcs = useBootPcs(scope, open);
  const stations = useBootStations(scope.organizationId, scope.venueId);
  const start = useStartBootEdit(scope);
  const [pcId, setPcId] = useState('');
  const [failure, setFailure] = useState<string | undefined>(undefined);

  // A PC that is playing is left out; with the floor unknown, every PC is offered and the api refuses a busy one.
  const choices = (pcs.data ?? []).filter((pc: BootPc) => pc.stationId === null || stations.get(pc.stationId)?.idle !== false);
  const label = (pc: BootPc): string => (pc.stationId === null ? pc.mac : `${stations.get(pc.stationId)?.label ?? pc.mac} · ${pc.mac}`);
  const chosen = choices.find((pc) => pc.id === pcId);

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFailure(undefined);
    if (pcId === '') {
      setFailure(t('boot.images.change.choose'));
      return;
    }
    start.mutate(
      { imageId: image.id, bootPcId: pcId },
      {
        onSuccess: () => {
          onClose();
          show({ tone: 'ok', title: t('boot.images.change.started', { name: chosen === undefined ? image.name : label(chosen) }) });
        },
        onError: (caught) => setFailure(problemDetail(caught) ?? t('boot.images.change.failed')),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())} size="sm" title={t('boot.images.change.title', { name: image.name })} description={t('boot.images.change.intro')}>
      {open ? (
        <form className="console-form" noValidate aria-label={t('boot.images.change.title', { name: image.name })} onSubmit={submit}>
          {failure === undefined ? null : (
            <Notice tone="danger" role="alert">
              {failure}
            </Notice>
          )}
          {pcs.data !== undefined && choices.length === 0 ? (
            <Notice tone="info">{t('boot.images.change.noIdle')}</Notice>
          ) : (
            <SelectField
              label={t('boot.images.change.pc')}
              description={t('boot.images.change.pcHint')}
              required
              placeholder={t('boot.images.change.pcPlaceholder')}
              value={pcId}
              onValueChange={setPcId}
              options={choices.map((pc) => ({ value: pc.id, label: label(pc) }))}
            />
          )}
          <div className="console-form__actions">
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" busy={start.isPending} disabled={choices.length === 0}>
              {t('boot.images.change.start')}
            </Button>
          </div>
        </form>
      ) : null}
    </Dialog>
  );
}

/** The letter a game disk is on; `G` until the api has given it one. */
const letterOf = (image: Pick<BootImage, 'driveLetter'>): string => image.driveLetter ?? 'G';

export function UpdateGamesDialog({ scope, image, open, onClose }: ImageDialogProps): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const start = useStartBootServerEdit(scope);
  const letter = letterOf(image);
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      size="md"
      title={t('boot.images.updateGames.title', { name: image.name })}
      description={t('boot.images.updateGames.body', { name: image.name, letter })}
      confirmLabel={t('boot.images.updateGames.confirm')}
      cancelLabel={t('common.cancel')}
      failedText={t('boot.images.updateGames.failed')}
      onConfirm={async () => {
        await start.mutateAsync(image.id);
        show({ tone: 'ok', title: t('boot.images.updateGames.started', { name: image.name, letter }) });
      }}
    >
      <p>{t('boot.images.updateGames.pcs')}</p>
      <p className="console-muted">{t('boot.images.updateGames.letter', { letter })}</p>
    </ConfirmDialog>
  );
}

export function SaveChangesDialog({ scope, image, open, onClose }: ImageDialogProps): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const publish = usePublishBootEdit(scope);
  const changedOn = useChangedOn(scope, image, open);
  const [note, setNote] = useState('');
  const [as, setAs] = useState<'version' | 'image'>('version');
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const edit = image.openEdit;
  // A change of Windows made on a PC may be saved as a new image of its own (a game disk's change is always a new version).
  const offersNewImage = edit?.target !== 'server' && image.kind === 'system';
  const asImage = offersNewImage && as === 'image';

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (edit === null) return;
    setFailure(undefined);
    const name = newName.trim();
    if (asImage && name === '') {
      setNameError(t('boot.images.save.newNameRequired'));
      return;
    }
    setNameError(undefined);
    publish.mutate(
      { editId: edit.id, note: note.trim(), ...(asImage ? { asNewImage: { name } } : {}) },
      {
        onSuccess: () => {
          setNote('');
          setNewName('');
          onClose();
          show({ tone: 'ok', title: asImage ? t('boot.images.save.doneNew', { name }) : t('boot.images.save.done', { name: image.name }) });
        },
        onError: (caught) => setFailure(problemDetail(caught) ?? t('boot.images.save.failed')),
      },
    );
  }

  const title =
    edit?.target === 'server' ? t('boot.images.save.titleServer') : changedOn === undefined ? t('boot.images.save.titleImage', { name: image.name }) : t('boot.images.save.title', { pc: changedOn });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? undefined : onClose())}
      size={offersNewImage ? 'md' : 'sm'}
      title={title}
      description={t(edit?.target === 'server' ? 'boot.images.save.introServer' : 'boot.images.save.intro')}
    >
      {open ? (
        <form className="console-form" noValidate aria-label={title} onSubmit={submit}>
          {failure === undefined ? null : (
            <Notice tone="danger" role="alert">
              {failure}
            </Notice>
          )}
          {offersNewImage ? (
            <RadioGroup
              label={t('boot.images.save.as')}
              value={as}
              onValueChange={(value) => setAs(value === 'image' ? 'image' : 'version')}
              options={[
                {
                  value: 'version',
                  label: (
                    <span className="console-boot__choice">
                      <span>{t('boot.images.save.asVersion', { name: image.name })}</span>
                      <span className="console-muted">{t('boot.images.save.asVersionHint', { name: image.name })}</span>
                    </span>
                  ),
                },
                {
                  value: 'image',
                  label: (
                    <span className="console-boot__choice">
                      <span>{t('boot.images.save.asImage')}</span>
                      <span className="console-muted">{t('boot.images.save.asImageHint', { name: image.name })}</span>
                    </span>
                  ),
                },
              ]}
            />
          ) : null}
          {asImage ? (
            <TextField
              label={t('boot.images.save.newName')}
              description={t('boot.images.save.newNameHint')}
              required
              autoComplete="off"
              value={newName}
              error={nameError}
              onChange={(event) => setNewName(event.target.value)}
            />
          ) : null}
          <TextField label={t('boot.images.save.note')} description={t('boot.images.save.noteHint')} showOptional autoComplete="off" value={note} onChange={(event) => setNote(event.target.value)} />
          <div className="console-form__actions">
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" busy={publish.isPending}>
              {asImage ? t('boot.images.save.confirmNew') : t('boot.images.save.confirm')}
            </Button>
          </div>
        </form>
      ) : null}
    </Dialog>
  );
}

export function ThrowAwayDialog({ scope, image, open, onClose }: ImageDialogProps): ReactNode {
  const { t } = useTranslation('console');
  const discard = useDiscardBootEdit(scope);
  const changedOn = useChangedOn(scope, image, open);
  const edit = image.openEdit;
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={
        edit?.target === 'server' ? t('boot.images.discard.titleServer') : changedOn === undefined ? t('boot.images.discard.titleImage', { name: image.name }) : t('boot.images.discard.title', { pc: changedOn })
      }
      description={t(edit?.target === 'server' ? 'boot.images.discard.bodyServer' : 'boot.images.discard.body')}
      confirmLabel={t('boot.images.discard.confirm')}
      cancelLabel={t('boot.images.discard.keep')}
      tone="danger"
      failedText={t('boot.images.discard.failed')}
      onConfirm={() => (edit === null ? Promise.resolve() : discard.mutateAsync(edit.id))}
    />
  );
}

// -- A game disk's drive letter ----------------------------------------------------------------------

export function DriveLetterDialog({ scope, image, open, onClose }: ImageDialogProps): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const update = useUpdateBootImage(scope);
  const images = useBootImages(scope, open);
  const [letter, setLetter] = useState(letterOf(image));
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const choices = freeDriveLetters(images.data ?? [image], image.id);
  const openOnServer = image.openEdit?.target === 'server';

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFailure(undefined);
    if (letter === image.driveLetter) {
      onClose();
      return;
    }
    update.mutate(
      { imageId: image.id, body: { driveLetter: letter } },
      {
        onSuccess: () => {
          onClose();
          show({ tone: 'ok', title: t('boot.images.driveLetter.saved') });
        },
        onError: (caught) => setFailure(problemDetail(caught) ?? t('boot.images.driveLetter.failed')),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())} size="sm" title={t('boot.images.driveLetter.title', { name: image.name })} description={t('boot.images.driveLetter.intro')}>
      {open ? (
        <form className="console-form" noValidate aria-label={t('boot.images.driveLetter.title', { name: image.name })} onSubmit={submit}>
          {failure === undefined ? null : (
            <Notice tone="danger" role="alert">
              {failure}
            </Notice>
          )}
          {openOnServer ? <Notice tone="info">{t('boot.images.driveLetter.openOnServer')}</Notice> : null}
          <SelectField
            label={t('boot.images.driveLetter.letter')}
            description={t('boot.images.driveLetter.hint')}
            required
            value={letter}
            onValueChange={setLetter}
            disabled={openOnServer}
            options={choices.map((one) => ({ value: one, label: `${one}:` }))}
          />
          <p className="console-muted">{t('boot.images.driveLetter.nextBoot')}</p>
          <div className="console-form__actions">
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" busy={update.isPending} disabled={openOnServer}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      ) : null}
    </Dialog>
  );
}

// -- NIC drivers -------------------------------------------------------------------------------------

/** "intel-i225, realtek-8125" → the families, trimmed and without repeats; at most the 32 the api takes. */
export function parseFamilies(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map((one) => one.trim()).filter((one) => one !== ''))].slice(0, 32);
}

export function NicDriversDialog({ scope, image, open, onClose }: ImageDialogProps): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const update = useUpdateBootImage(scope);
  const [mode, setMode] = useState<BootNicDriverMode>(image.nicDriverMode);
  const [families, setFamilies] = useState(image.nicFamilies.join(', '));
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const parsed = parseFamilies(families);

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFailure(undefined);
    if (mode === 'families' && parsed.length === 0) {
      setFailure(t('boot.images.nic.familiesRequired'));
      return;
    }
    update.mutate(
      { imageId: image.id, body: { nicDriverMode: mode, ...(mode === 'families' ? { nicFamilies: parsed } : {}) } },
      {
        onSuccess: () => {
          onClose();
          show({ tone: 'ok', title: t('boot.images.nic.saved') });
        },
        onError: (caught) => setFailure(problemDetail(caught) ?? t('boot.images.nic.failed')),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())} size="sm" title={t('boot.images.nic.title', { name: image.name })} description={t('boot.images.nic.intro')}>
      {open ? (
        <form className="console-form" noValidate aria-label={t('boot.images.nic.title', { name: image.name })} onSubmit={submit}>
          {failure === undefined ? null : (
            <Notice tone="danger" role="alert">
              {failure}
            </Notice>
          )}
          <SegmentedControl
            label={t('boot.images.nic.mode')}
            value={mode}
            onValueChange={setMode}
            options={[
              { value: 'auto', label: t('boot.images.nic.modes.auto') },
              { value: 'families', label: t('boot.images.nic.modes.families') },
              { value: 'broad', label: t('boot.images.nic.modes.broad') },
            ]}
          />
          <p className="console-muted">{t(`boot.images.nic.hint.${mode}`)}</p>
          {mode === 'families' ? (
            <TextField label={t('boot.images.nic.families')} description={t('boot.images.nic.familiesHint')} autoComplete="off" value={families} onChange={(event) => setFamilies(event.target.value)} />
          ) : null}
          <p className="console-muted">{t('boot.images.nic.nextVersion')}</p>
          <div className="console-form__actions">
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" busy={update.isPending}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      ) : null}
    </Dialog>
  );
}
